import { create } from "zustand";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import type { ParseKeys } from "i18next";

import { type ILocaleType } from "@configs";
import type {
  IAlertProps,
  IShowAlertParams,
} from "@components/molecules/alert";
import { ALERT_DEFAULT_PROPS } from "@components/molecules/alert/config";
import { IThemeMode } from "@/theme/types";
import type { IBreadcrumb } from "@/routes/types";

export type IPageMode = "index" | "edit" | "view" | "create";
interface IUiState {
  locale: ILocaleType;
  changeLocale: (value: ILocaleType) => void;
  theme: IThemeMode;
  setTheme: (value: IThemeMode) => void;
  alertProps: IAlertProps;
  showAlert: (props: IAlertProps) => void;
  pageTitle: ParseKeys<"pageTitle">;
  pageTitleName?: { value: string; pathname: string };
  setPageTitleName: (value: string, pathname: string) => void;
  breadcrumbs: IBreadcrumb | null;
  pageMode: IPageMode;

  /**
   * DO NOT USE THIS. I ONLY WRITE IT FOR USING IN STORYBOOK
   * @param value
   * @returns
   */
  setPageMode: (value: IPageMode) => void;
  setPage: (
    request: Request,
    pageTitle: ParseKeys<"pageTitle">,
    breadcrumbs: IBreadcrumb | null,
    pageMode: IPageMode,
  ) => void;

  /**
   * the path that has been set for pageTitle, pageMode and pageBreadcrumbs
   */
  locatedPath: string;
}

// partialized items are stored in localStorge
export const useUIStore = create<IUiState>()(
  subscribeWithSelector(
    persist(
      devtools(
        (set) => ({
          locale: "fa",
          changeLocale: (value) => {
            set({ locale: value });
          },
          theme: IThemeMode.DEFAULT,
          setTheme: (value) => {
            set({ theme: value });
          },
          alertProps: ALERT_DEFAULT_PROPS,
          showAlert: (value) => {
            set({ alertProps: value });
          },
          pageTitle: "mainPage",
          locatedPath: "",
          pageMode: "create",
          breadcrumbs: null,
          setPageMode(value) {
            set({ pageMode: value });
          },
          pageTitleName: undefined,
          setPageTitleName(value, pathname) {
            set({
              pageTitleName: {
                value,
                pathname,
              },
            });
          },
          setPage(request, pageTitle, breadcrumbs, pageMode) {
            set({
              pageTitle,
              locatedPath: request.url,
              breadcrumbs,
              pageMode,
            });
          },
        }),
        {
          enabled: !import.meta.env.PROD,
          name: "uiStore",
        },
      ),
      {
        name: "uiState",
        partialize: (state) => ({
          locale: state.locale,
          theme: state.theme,
        }),
      },
    ),
  ),
);

/**
 * Shows a generic alert dialog.
 * @param {IShowAlertParams} alertProps - The properties of the alert.
 */
export const showAlert = (alertProps: IShowAlertParams) => {
  useUIStore.setState(() => ({
    alertProps: {
      ...alertProps,
      open: true,
    },
  }));
};

/**
 * Closes the currently open alert dialog.
 */
export const closeAlert = () => {
  useUIStore.setState(() => ({ alertProps: ALERT_DEFAULT_PROPS }));
};

export const uiStorFn = {
  setTheme: useUIStore.getState().setTheme,
  setPage: useUIStore.getState().setPage,
  setPageMode: useUIStore.getState().setPageMode,
  setPageTitleName: useUIStore.getState().setPageTitleName,
};
